import type { ComponentType } from "react";
import { useLayoutEffect, useRef } from "react";
import { CircleDashedIcon } from "lucide-react";

import { Badge } from "./badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./card";
import { SurfaceCard } from "./surface-card";
import { cn } from "../../lib/utils";

export enum KpiIntent {
  Neutral = "neutral",
  Info = "info",
  Success = "success",
  Warning = "warning",
  Danger = "danger",
}

export type KpiCardItem = {
  key: string;
  label: string;
  value: string | number;
  helperText: string;
  icon: ComponentType<{ className?: string }>;
  intent?: KpiIntent;
  /** When omitted, the progress bar and percentage badge are hidden. */
  percentage?: number;
  percentageLabel?: string;
};

type KpiCardGridProps = {
  items: KpiCardItem[];
  columnsClassName?: string;
  valueClassName?: string;
  cardClassName?: string;
  density?: "default" | "compact";
  variant?: "default" | "dashboard";
  balanceRows?: boolean;
  maxColumns?: number;
  responsiveMode?: "viewport" | "container";
  /**
   * Phone layout for the KPI row. `"scroll"` (default) renders a native-app
   * horizontal snap rail so cards don't wrap into tall stacks that bury the
   * content below. `"grid"` keeps the legacy wrapping grid. From `md:`
   * (or `@4xl/kpi` in container mode) both modes render the identical desktop
   * grid, so this only affects narrow screens.
   */
  mobileLayout?: "scroll" | "grid";
  /**
   * When the scroll rail is active, let it bleed past the standard page gutter
   * to the screen edges so cards scroll edge-to-edge (the page gutter only
   * shows as the inset on the first/last card). Enable this ONLY when the grid
   * is a top-level page section sitting directly inside the page gutter — not
   * when it's nested inside a padded panel/card, where the bleed would escape
   * its container. No effect once the desktop grid re-engages.
   */
  edgeToEdge?: boolean;
};

export function KpiCardGrid({
  items,
  columnsClassName,
  valueClassName,
  cardClassName,
  density = "default",
  variant = "dashboard",
  balanceRows = false,
  maxColumns = 5,
  responsiveMode = "viewport",
  mobileLayout = "scroll",
  edgeToEdge = false,
}: KpiCardGridProps) {
  const DashboardSurface = SurfaceCard ?? Card;
  const isCompact = variant === "dashboard" && density === "compact";
  const isContainerResponsive = responsiveMode === "container";
  const balancedColumns = balanceRows ? resolveBalancedDesktopColumns(items.length, maxColumns) : null;
  const balancedPlaceholderCount = balancedColumns ? getPlaceholderCount(items.length, balancedColumns) : 0;
  const resolvedColumnsClassName =
    balanceRows && balancedColumns
      ? cn("grid", isCompact ? "gap-2.5" : "gap-3", getBalancedGridClassName(balancedColumns, responsiveMode))
      : (columnsClassName ?? getDefaultGridClassName(responsiveMode));

  // A lone tile has nothing to scroll past, so keep it full-width in the grid.
  const useScrollRail = mobileLayout === "scroll" && items.length > 1;
  const containerClassName =
    useScrollRail ?
      cn(
        getScrollRailBaseClassName(responsiveMode),
        edgeToEdge && getRailBleedClassName(responsiveMode),
        withoutBaseGridDisplay(resolvedColumnsClassName),
      )
    : resolvedColumnsClassName;
  const scrollItemClassName = useScrollRail ? "shrink-0 basis-[78%] snap-start" : undefined;

  const grid = (
      <div className={containerClassName}>
        {items.map((item) => {
          const intent = item.intent ?? KpiIntent.Neutral;
          const hasProgressFooter =
            typeof item.percentage === "number" &&
            Number.isFinite(item.percentage) &&
            typeof item.percentageLabel === "string" &&
            item.percentageLabel.trim().length > 0;
          const Wrapper = variant === "dashboard" ? DashboardSurface : Card;
          const IconComponent = item.icon ?? CircleDashedIcon;
          return (
            <Wrapper
              key={item.key}
              className={cn(
                variant === "dashboard"
                  ? cn(
                      "@container/kpi-card text-card-foreground group/card flex min-w-0 flex-col overflow-hidden text-sm",
                      isCompact ? "h-[8.5rem] gap-3 py-4" : "gap-3 py-3",
                    )
                  : "border-border/70 bg-card/80 shadow-[var(--shadow-card)]",
                scrollItemClassName,
                cardClassName,
              )}
            >
              <CardHeader
                className={cn("px-3", isContainerResponsive ? "@sm/kpi:px-4" : "sm:px-4", isCompact ? "gap-2 pb-1" : "pb-1")}
              >
                <div className='flex min-w-0 items-start justify-between gap-2'>
                  <CardDescription
                    className={cn(
                      // Sentence case, as the labels are written: no shouting capitals on the stat cards.
                      "min-w-0 flex-1 truncate font-semibold",
                      // Keep KPI labels legible on phones (≥12px); only go to 11px on larger viewports.
                      isCompact ? "text-xs md:text-[11px]" : "text-xs",
                    )}
                  >
                    {item.label}
                  </CardDescription>
                  <span
                    className={cn(
                      "inline-flex shrink-0 items-center justify-center rounded-md border",
                      isCompact ? "size-[1.125rem]" : "size-5",
                      getBadgeClassName(intent),
                    )}
                  >
                    <IconComponent className={isCompact ? "size-2" : "size-2.5"} />
                  </span>
                </div>
                <CardTitle className={cn("min-w-0 max-w-full leading-none font-semibold tracking-tight tabular-nums", valueClassName, getValueClassName(intent))}>
                  <AutoFitValue value={item.value} {...getKpiFontSizeRange(isCompact, variant)} />
                </CardTitle>
              </CardHeader>
              <CardContent
                className={cn(
                  "mt-auto min-w-0 px-3 pt-0",
                  isContainerResponsive ? "@sm/kpi:px-4" : "sm:px-4",
                  isCompact ? "space-y-1.5" : "space-y-1.5",
                )}
              >
                {hasProgressFooter ?
                  <>
                    <div className='bg-muted h-0.5 overflow-hidden rounded-full'>
                      <div
                        className={cn("h-full rounded-full", getBarClassName(intent))}
                        style={{ width: `${Math.min(100, Math.max(8, item.percentage ?? 0))}%` }}
                      />
                    </div>
                    <div
                      className={cn(
                        "flex min-w-0 items-center justify-between gap-2",
                        isCompact ? "text-xs" : "text-sm",
                      )}
                    >
                      <span className='text-muted-foreground min-w-0 flex-1 truncate'>{item.helperText}</span>
                      <Badge
                        variant='outline'
                        className={cn("rounded-full px-1.5 py-0", "text-xs")}
                      >
                        {item.percentageLabel}
                      </Badge>
                    </div>
                  </>
                : <p
                    className={cn(
                      "text-muted-foreground min-w-0 truncate leading-relaxed",
                      isCompact ? "text-xs" : "text-sm",
                    )}
                  >
                    {item.helperText}
                  </p>
                }
              </CardContent>
            </Wrapper>
          );
        })}
        {Array.from({ length: balancedPlaceholderCount }).map((_, index) => (
          <div
            key={`placeholder-${index}`}
            aria-hidden='true'
            className={isContainerResponsive ? "hidden @5xl/kpi:block" : "hidden lg:block"}
          />
        ))}
      </div>
  );

  return isContainerResponsive ? <div className='@container/kpi'>{grid}</div> : grid;
}

/**
 * Min/max font size (px) the KPI value is allowed to occupy. The value shrinks
 * from `max` down toward `min` only as far as needed to fit on a single line —
 * see {@link AutoFitValue}. `min` is deliberately small so very large numbers
 * (e.g. "13 330,00 $") fit without wrapping or overflowing tiny tiles.
 */
function getKpiFontSizeRange(isCompact: boolean, variant: KpiCardGridProps["variant"]) {
  // Defaults intentionally kept moderate: the AutoFitValue layer will
  // shrink the number whenever the container narrows, so a smaller
  // `max` here is what makes the *default* (wide tile) presentation
  // feel proportioned. Bumping `max` was making the number dominate
  // the card on roomy desktop layouts.
  if (variant === "dashboard") {
    return isCompact ? { min: 14, max: 30 } : { min: 16, max: 34 };
  }

  return { min: 16, max: 32 };
}

type AutoFitValueProps = {
  value: string | number;
  /** Smallest font size (px) the value may shrink to. */
  min: number;
  /** Largest font size (px) the value may grow to. */
  max: number;
};

/**
 * Renders a single-line value that sizes itself to the available width: it
 * starts at `max` and scales down toward `min` only as much as needed so the
 * text never wraps, truncates, or overflows. It re-measures on container
 * resize (via `ResizeObserver`) and whenever the value changes.
 */
function AutoFitValue({ value, min, max }: AutoFitValueProps) {
  const containerRef = useRef<HTMLSpanElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const text = textRef.current;
    if (!container || !text) return;

    const fit = () => {
      const available = container.clientWidth;
      if (available <= 0) return;
      // Measure intrinsic width at the largest size, then scale linearly:
      // for a single, non-wrapping line, text width is proportional to font
      // size, so one pass lands exactly on the fitting size.
      text.style.fontSize = `${max}px`;
      const intrinsic = text.scrollWidth;
      if (intrinsic <= 0) return;
      const next = Math.max(min, Math.min(max, (available / intrinsic) * max));
      text.style.fontSize = `${next}px`;
    };

    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(container);
    return () => observer.disconnect();
  }, [value, min, max]);

  return (
    <span ref={containerRef} className='block min-w-0 max-w-full overflow-hidden'>
      <span ref={textRef} className='inline-block whitespace-nowrap leading-none' style={{ fontSize: max }}>
        {value}
      </span>
    </span>
  );
}

function resolveBalancedDesktopColumns(count: number, maxColumns: number) {
  const safeCount = Math.max(1, count);
  const limit = Math.max(1, Math.min(maxColumns, safeCount));

  /**
   * A single-column layout minimises placeholders but defeats responsive KPI grids
   * (every card stretches full width). Once we have more than one tile and space for
   * more than one column, only consider widths >= 2.
   */
  const loopStart =
    safeCount <= 1 || limit <= 1 ?
      1
    : 2;

  let bestColumns = limit;
  let bestScore = Number.POSITIVE_INFINITY;

  for (let columns = loopStart; columns <= limit; columns += 1) {
    const placeholders = getPlaceholderCount(safeCount, columns);
    const rows = Math.ceil(safeCount / columns);
    const score = placeholders * 10 + rows;

    if (score < bestScore || (score === bestScore && columns > bestColumns)) {
      bestScore = score;
      bestColumns = columns;
    }
  }

  return bestColumns;
}

function getPlaceholderCount(count: number, columns: number) {
  const remainder = count % columns;
  return remainder === 0 ? 0 : columns - remainder;
}

/**
 * KPI tiles are 2-per-row on phones (a native-dashboard feel for overview
 * pages — home, activities, finance overview). The value auto-fits and the
 * label/helper truncate, so two-up stays legible on narrow screens. The
 * 2→3 step is gated on `md:` / `@4xl/kpi` (≈768px) and the multi-column
 * desktop layout kicks in above that as before.
 */
/**
 * Mobile = a horizontal snap rail; desktop re-enables the normal grid at the
 * same breakpoint every caller already steps up at (`md:` for viewport,
 * `@4xl/kpi` for container). The next card peeks past the edge so the rail
 * reads as scrollable, and the scrollbar is hidden for a native feel. `py-1`
 * keeps card shadows from being clipped by the scroll container.
 */
function getScrollRailBaseClassName(responsiveMode: KpiCardGridProps["responsiveMode"]) {
  const base =
    "flex snap-x snap-mandatory overflow-x-auto py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden";
  return responsiveMode === "container" ?
      cn(base, "@4xl/kpi:grid @4xl/kpi:snap-none @4xl/kpi:overflow-visible @4xl/kpi:py-0")
    : cn(base, "md:grid md:snap-none md:overflow-visible md:py-0");
}

/**
 * Full-bleed offset so the rail scrolls to the screen edges instead of being
 * trapped inside the page gutter — the gutter then only appears as the inset
 * on the first/last card. The negative margin + matching padding cancel and
 * re-add the standard tenant page gutter (`px-4 sm:px-6`), and both reset once
 * the desktop grid re-engages so the row re-aligns with the rest of the page.
 *
 * `scroll-px-*` mirrors the padding: without it `snap-mandatory` aligns the
 * first card flush to the scrollport edge and scrolls the leading padding out
 * of view, so the card would touch the screen edge. The scroll padding insets
 * the snap port so the leading/trailing gutter actually shows.
 *
 * Container mode skips the `sm:` step to avoid mixing media- and
 * container-query overrides on the same property (a left-over ~8px inset on
 * 640–768px tablets is the only cost; phones are exact).
 */
function getRailBleedClassName(responsiveMode: KpiCardGridProps["responsiveMode"]) {
  return responsiveMode === "container" ?
      "-mx-4 px-4 scroll-px-4 @4xl/kpi:mx-0 @4xl/kpi:px-0 @4xl/kpi:scroll-px-0"
    : "-mx-4 px-4 scroll-px-4 sm:-mx-6 sm:px-6 sm:scroll-px-6 md:mx-0 md:px-0 md:scroll-px-0";
}

/**
 * Strips the bare `grid` display token (which would force `display: grid` at
 * every width and override the rail's `flex`) while keeping `grid-cols-*`,
 * `md:grid-cols-*`, gap, and other utilities intact — those are inert under
 * flex on mobile and take effect once the desktop grid is re-enabled.
 */
function withoutBaseGridDisplay(className: string): string {
  return className
    .split(/\s+/)
    .filter((token) => token.length > 0 && token !== "grid")
    .join(" ");
}

function getDefaultGridClassName(responsiveMode: KpiCardGridProps["responsiveMode"]) {
  if (responsiveMode === "container") {
    return "grid gap-2.5 grid-cols-2 @4xl/kpi:grid-cols-3 @5xl/kpi:grid-cols-4 @7xl/kpi:grid-cols-5";
  }

  return "grid gap-2.5 grid-cols-2 md:grid-cols-3 lg:grid-cols-5";
}

function getBalancedGridClassName(columns: number, responsiveMode: KpiCardGridProps["responsiveMode"]) {
  // Phones (viewport / container < 768px) render 2-up (a lone tile stays
  // full width). From tablet width (≥ md / ≥ @4xl/kpi) the row ramps up to
  // the desktop layout; the 4-of-5 intermediate is skipped so the row jumps
  // from 3 columns straight to its full width — a transitional "4 + 1
  // orphan" row looks broken on desktop.
  if (responsiveMode === "container") {
    if (columns <= 1) return "grid-cols-1";
    if (columns === 2) return "grid-cols-2";
    if (columns === 3) return "grid-cols-2 @4xl/kpi:grid-cols-3";
    if (columns === 4) return "grid-cols-2 @4xl/kpi:grid-cols-3 @5xl/kpi:grid-cols-4";
    if (columns === 5) return "grid-cols-2 @4xl/kpi:grid-cols-3 @5xl/kpi:grid-cols-5";
    if (columns >= 6) return "grid-cols-2 @4xl/kpi:grid-cols-3 @5xl/kpi:grid-cols-5 @7xl/kpi:grid-cols-6";
    return "grid-cols-2 @5xl/kpi:grid-cols-5";
  }

  if (columns <= 1) return "grid-cols-1";
  if (columns === 2) return "grid-cols-2";
  if (columns === 3) return "grid-cols-2 md:grid-cols-3";
  if (columns === 4) return "grid-cols-2 md:grid-cols-3 lg:grid-cols-4";
  if (columns === 5) return "grid-cols-2 md:grid-cols-3 lg:grid-cols-5";
  if (columns >= 6) return "grid-cols-2 md:grid-cols-3 lg:grid-cols-5 2xl:grid-cols-6";
  return "grid-cols-2 md:grid-cols-5";
}

function getBadgeClassName(intent: KpiIntent) {
  if (intent === KpiIntent.Info) {
    return "border-sky-200 bg-sky-100 text-sky-700 dark:border-sky-900 dark:bg-sky-950 dark:text-sky-300";
  }
  if (intent === KpiIntent.Success) {
    return "border-emerald-200 bg-emerald-100 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300";
  }
  if (intent === KpiIntent.Warning) {
    return "border-amber-200 bg-amber-100 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300";
  }
  if (intent === KpiIntent.Danger) {
    return "border-rose-200 bg-rose-100 text-rose-700 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-300";
  }
  return "border-border bg-muted text-muted-foreground";
}

function getBarClassName(intent: KpiIntent) {
  if (intent === KpiIntent.Info) return "bg-sky-500/80";
  if (intent === KpiIntent.Success) return "bg-emerald-500/80";
  if (intent === KpiIntent.Warning) return "bg-amber-500/80";
  if (intent === KpiIntent.Danger) return "bg-rose-500/80";
  return "bg-zinc-500/80";
}

function getValueClassName(intent: KpiIntent) {
  if (intent === KpiIntent.Info) return "text-sky-600";
  if (intent === KpiIntent.Success) return "text-emerald-600";
  if (intent === KpiIntent.Warning) return "text-amber-600";
  if (intent === KpiIntent.Danger) return "text-rose-600";
  return "text-foreground";
}
